import { Button, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap";
import Paginations from "../pagination";
import { toast } from "react-toastify";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import Breadcrumbs from "../breadcrumbs";
import { useSearchParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import { FaLink, FaTimes, FaYoutube } from "react-icons/fa";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const ContentDataTable = ({ title, apiEndpoint, defaultValue = "all" }) => {
  const [searchParams] = useSearchParams();
  const [valueFromUrl, setValueFromUrl] = useState(searchParams.get("value") || defaultValue);

  const [tableData, setTableData] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [paginationData, setPaginationData] = useState({
    current_page: 1,
    page_size: 0,
    total_page: 1,
    total_records: 0,
  });
  const [page, setPage] = useState(1);
  const [showSpinner, setShowSpinner] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [buttonsLoading, setButtonsLoading] = useState({
    disabled: false, // global disable to prevent concurrent operations
    copy: false,
    csv: false,
    excel: false,
    pdf: false,
    print: false,
  });
  

  const debounceTimeoutRef = useRef(null);

  // Debounce search text
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    debounceTimeoutRef.current = timer;

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, [searchText]);

  const loadItems = useCallback(() => {
    setShowSpinner(true);
    setSelectedIds([]);

    const url = `${process.env.REACT_APP_API_URL}${apiEndpoint}`;
    const params = new URLSearchParams({
      page,
      per_page: 20,
      value: valueFromUrl,
      search: debouncedSearchText?.trim() ?? "",
      sort_by: sortBy,
      sort_order: sortOrder
    });

    axios
      .get(`${url}?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      .then((response) => {
        const data = response.data.data || [];
        setTableData(data);
        setPaginationData(
          response.data.page_data ?? {
            current_page: 1,
            page_size: 0,
            total_page: 1,
            total_records: 0,
          },
        );
      })
      .catch((error) => {
        const { message } = error?.response?.data;
        console.error("Error fetching data:", error);
        toast.error(message || "Error fetching data");
      })
      .finally(() => {
        setShowSpinner(false);
      });
  }, [page, valueFromUrl, debouncedSearchText, apiEndpoint, sortBy, sortOrder]);

  // Reset page and search when URL value changes
  useEffect(() => {
    const newValue = searchParams.get("value") || defaultValue;
    if (newValue !== valueFromUrl) {
      setValueFromUrl(newValue);
      setPage(1);
      setSearchText("");
      setDebouncedSearchText("");
      setSelectedIds([]);
    }
  }, [searchParams, valueFromUrl, defaultValue]);

  // Load data when page or filters change
  useEffect(() => {
    // if (page) {
      loadItems();
    // }
  }, [page, sortBy, sortOrder, debouncedSearchText, valueFromUrl]);


  const handleSort = (column) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return prev;
      } else {
        setSortOrder("desc");
        return column;
      }
    });

    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(tableData.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleClear = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    setSearchText("");
    setDebouncedSearchText("");
    setPage(1);
    setSelectedIds([]);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);
  // Add these helper functions
  const getTableHeaders = () => {
    return ["Course ID", "Course Name", "Section Name", "Content URLs"];
  };

  const getRowData = (row) => {
    return [
      row?.course_id || "",
      row?.shortname || "",
      row?.section_name || "",
      row?.video_url || "",
    ];
  };

      // Add: helper to fetch export data (use page = 0 per request)
    const fetchExportData = async () => {
     const url = `${process.env.REACT_APP_API_URL}${apiEndpoint}`;
      const params = new URLSearchParams({
        page: 0, // request 0 to get all records (server-side behavior)
        per_page: 20,
        value: valueFromUrl,
        search: debouncedSearchText?.trim() ?? "",
      });
  
      try {
        const response = await axios.get(`${url}?${params.toString()}`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        return response.data.data || [];
      } catch (err) {
        console.error("Error fetching export data:", err);
        toast.error("Failed to fetch data for export");
        return [];
      }
    };

  const handleCopy = () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((prev) => ({ ...prev, disabled: true, copy: true }));
    const headers = getTableHeaders();
    const dataToExport =
      selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : tableData;
    const rows = dataToExport.map((row) => getRowData(row));

    let textData = headers.join("\t") + "\n";
    rows.forEach((row) => {
      textData += row.join("\t") + "\n";
    });

    navigator.clipboard
      .writeText(textData)
      .then(() => {
        toast.success(`copied to clipboard!`);
      })
      .catch((err) => {
        toast.error("Failed to copy data");
        console.error("Copy error:", err);
      })
      .finally(() => {
        setButtonsLoading((prev) => ({ ...prev, disabled: false, copy: false }));
      });
  };

  const handleCSV = async () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((prev) => ({ ...prev, disabled: true, csv: true }));
    const headers = getTableHeaders();
    const dataToExport =
      selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : await fetchExportData();
    const rows = dataToExport.map((row) => getRowData(row));

    let csvContent = headers.map((h) => `"${h}"`).join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.map((cell) => `"${cell}"`).join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `content_report_${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file downloaded successfully.");
    setButtonsLoading((prev) => ({ ...prev, disabled: false, csv: false }));
  };

  const handleExcel = async () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((prev) => ({ ...prev, disabled: true, excel: true }));
    const headers = getTableHeaders();
    const dataToExport =
      selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : await fetchExportData();
    const rows = dataToExport.map((row) => getRowData(row));

    let excelContent =
      '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Sheet1</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table>';

    excelContent += "<tr>";
    headers.forEach((header) => {
      excelContent += `<th style="background-color: #4472C4; color: white; font-weight: bold; border: 1px solid #000;">${header}</th>`;
    });
    excelContent += "</tr>";

    rows.forEach((row) => {
      excelContent += "<tr>";
      row.forEach((cell) => {
        excelContent += `<td style="border: 1px solid #000;">${cell}</td>`;
      });
      excelContent += "</tr>";
    });

    excelContent += "</table></body></html>";

    const blob = new Blob([excelContent], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `content_report_${Date.now()}.xls`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Excel file downloaded successfully.");
    setButtonsLoading((prev) => ({ ...prev, disabled: false, excel: false }));
  };

  const handlePDF = async () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((s) => ({ ...s, disabled: true, pdf: true }));

    try {
      const url = `${process.env.REACT_APP_API_URL}${apiEndpoint}`;
      const params = new URLSearchParams({
        page: 0, // request zero-based page so server will export full set
        per_page: 20,
        value: valueFromUrl,
        search: debouncedSearchText?.trim() ?? "",
        file_type: "pdf",
      });

      const resp = await axios.get(`${url}?${params.toString()}`, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });

      const data = resp?.data ?? {};
      if (data.success) {
        const msg = data.message || data.filename || "";
        if (!msg) {
          toast.error("Export returned no filename");
        } else {
          // message may be a full URL or just a filename — handle both
          const downloadUrl = msg.startsWith("http")
            ? msg
            : `${process.env.REACT_APP_S3VIDEO}${msg}`;

        // Step 1: Fetch file as blob → this guarantees download finished
          const fileResp = await axios.get(downloadUrl, { responseType: "blob" });

          const blobUrl = URL.createObjectURL(fileResp.data);

          // Step 2: Trigger browser download
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = (downloadUrl.split("/").pop() || "export.pdf").split("?")[0];
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(blobUrl);
         toast.success("PDF file downloaded successfully.");
        }
      } else {
        toast.error(data.message || "Failed to generate PDF on server");
      }
    } catch (err) {
      console.error("PDF export error:", err);
      toast.error("Failed to request PDF export");
    } finally {
      setButtonsLoading((s) => ({ ...s, pdf: false, disabled: false }));
    }
  };

  const handlePrint = () => {
    if (buttonsLoading.disabled) return;
    setButtonsLoading((prev) => ({ ...prev, disabled: true, print: true }));
    const headers = getTableHeaders();
    const dataToExport =
      selectedIds.length > 0 ? tableData.filter((row) => selectedIds.includes(row.id)) : tableData;
    const rows = dataToExport.map((row) => getRowData(row));

    // Create a hidden iframe for printing
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const printContent = `
  <!DOCTYPE html>
  <html>
  <head>
    <title>${title}</title>
    <style>
      @media print {
        @page { size: landscape; margin: 10mm; }
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        table { page-break-inside: auto; }
        tr { page-break-inside: avoid; page-break-after: auto; }
        thead { display: table-header-group; }
      }
      body { font-family: Arial, sans-serif; margin: 20px; background-color: #ffffff; }
      h2 { color: #333; margin-bottom: 10px; }
      p { margin-bottom: 20px; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th { background-color: #4472C4 !important; color: #ffffff !important; padding: 8px; text-align: left; border: 1px solid #ddd; font-weight: bold; font-size: 11px; word-wrap: break-word; white-space: normal; }
      td { padding: 8px; border: 1px solid #ddd; font-size: 10px; word-wrap: break-word; white-space: normal; overflow-wrap: break-word; }
      tr:nth-child(even) { background-color: #f9f9f9; }
    </style>
  </head>
  <body>
    <h2>${title}</h2>
    <p>Generated on: ${new Date().toLocaleString()}</p>
    <table>
      <thead>
        <tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>
      </thead>
      <tbody>
        ${rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("")}
      </tbody>
    </table>
  </body>
  </html>
  `;

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();

    // Wait for iframe content to load, then print and remove iframe
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Remove iframe after ensuring print dialog opens
        setTimeout(() => {
          document.body.removeChild(iframe);       
          setButtonsLoading((prev) => ({ ...prev, disabled: false, print: false }));
        }, 1000);
      }, 500);
    };
  };

  const TableCellWithTooltip = ({ children, maxLength = 20, style = {}, ...rest }) => {
    const text = children?.toString() || "";
    const isOverflow = text.length > maxLength;

    const displayText = isOverflow ? text.substring(0, maxLength) + "..." : text;

    return (
      <td
        {...rest}
        style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...style }}
      >
        {isOverflow ? (
          <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
            <span style={{ cursor: "default" }}>{displayText}</span>
          </OverlayTrigger>
        ) : (
          <span>{displayText}</span>
        )}
      </td>
    );
  };

  const SortableHeader = ({ label, column }) => {
    const isActive = sortBy === column;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => handleSort(column)}
      >
        <span>{label}</span>
        <img
          src="/images/sorting-icon.svg"
          alt="sort"
          style={{
            width: "12px",
            display: "inline-block",
            opacity: isActive ? 1 : 0.4,
            transform: isActive && sortOrder === "desc" ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </div>
    );
  };



  return (
    <Container fluid className="p-4">
      <Breadcrumbs />
      <p className="section-title mb-4">{title}</p>
      <Row className="align-items-end mb-3">
        <Col md="3">
          <Form.Group className="mb-2 text-start w-100">
            <Form.Label className="mb-1">Search</Form.Label>
            <div className="position-relative w-100">
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setPage(1);
                }}
                style={{
                  paddingRight: "35px", // space for the icon
                }}
              />
              {searchText.trim() && (
                <FaTimes
                  onClick={() => setSearchText("")}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#999",
                    fontSize: "16px",
                  }}
                />
              )}
            </div>
          </Form.Group>
        </Col>
        <Col md="9" className="d-flex justify-content-end align-items-end mb-2">
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Button size="sm" onClick={handleCopy} disabled={tableData.length === 0 || buttonsLoading.disabled}>
               {buttonsLoading.copy ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />Copy
                </>
              ) : (
                "Copy"
              )}
            </Button>
            <Button size="sm" onClick={handleCSV} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.csv ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />CSV
                </>
              ) : (
                "CSV"
              )}
            </Button>
            <Button size="sm" onClick={handleExcel} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.excel ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />Excel
                </>
              ) : (
                "Excel"
              )}
            </Button>
            <Button size="sm" onClick={handlePDF} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.pdf ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />PDF
                </>
              ) : (
                "PDF"
              )}
            </Button>
            <Button size="sm" onClick={handlePrint} disabled={tableData.length === 0 || buttonsLoading.disabled}>
              {buttonsLoading.print ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" />Print
                </>
              ) : (
                "Print"
              )}
            </Button>
          </div>
        </Col>
      </Row>

      <Row>
        <Table
          responsive
          className="scribe-contents-table"
          // style={{ tableLayout: "fixed", width: "100%" }}
        >
          <thead>
            <tr>
              {false &&
              <th style={{ width: "2%" }}>
                <Form.Check
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedIds.length > 0 && selectedIds.length === tableData?.length}
                />
              </th>
              }
              <th style={{ width: "8%" }}><SortableHeader label="Course ID" column="course_id" /></th>
              <th style={{ width: "10%" }}><SortableHeader label="Course Name" column="shortname" /></th>
              <th style={{ width: "25%" }}><SortableHeader label="Section Name" column="section_name" /></th>
              <th style={{ width: "25%" }}><SortableHeader label="Content URLs" column="content" /></th>
            </tr>
          </thead>
          <tbody>
            {showSpinner ? (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", padding: "4rem 0" }}>
                  <Button
                    variant="link"
                    className="mt-2 mb-5 btn-spinner ms-0 text-decoration-none"
                    disabled
                  >
                    <Spinner
                      as="span"
                      animation="grow"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-1"
                    />
                  </Button>
                </td>
              </tr>
            ) : tableData?.length > 0 ? (
              tableData.map((row, rowIndex) => (
                <tr key={`${row?.id}-${rowIndex}`} style={{ height: "30px" }}>
                  {/* Checkbox */}
                  {false &&
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedIds.includes(row?.id)}
                      onChange={() => handleSelectRow(row?.id)}
                    />
                  </td>
                  }

                  {/* Course ID */}
                  <TableCellWithTooltip maxLength={25}>{row?.course_id}</TableCellWithTooltip>

                  {/* Course Name */}
                  <TableCellWithTooltip maxLength={40}>{row?.shortname}</TableCellWithTooltip>

                  {/* Section Name */}
                  <TableCellWithTooltip maxLength={65}>{row?.section_name}</TableCellWithTooltip>

                  {/* Content URLs */}
                  <td>
                    {row?.video_url ? (
                      <div className="d-inline-flex align-items-center gap-2">
                        {/* Icon */}
                        {row.video_url.includes("youtube") || row.video_url.includes("youtu.be") ? (
                          <FaYoutube color="red" size={20} />
                        ) : (
                          <FaLink size={18} />
                        )}

                        {/* Link with tooltip and truncation */}
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`tooltip-${row?.id ?? rowIndex}`}>{row.video_url}</Tooltip>}
                        >
                          <a
                            href={row.video_url}
                            target="_blank"
                            style={{
                              cursor: "pointer",
                              // color: row?.visited_updated && pingType === "web" ? "" : "#007bff",
                              textDecoration: "underline",
                              maxWidth: "350px",
                              display: "inline-block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row.video_url.length > 60
                              ? row.video_url.substring(0, 60) + "..."
                              : row.video_url}
                          </a>
                        </OverlayTrigger>
                      </div>
                    ) : (
                      ""
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", padding: "2rem" }}>
                  <img
                    src="/images/no-data-found.jpeg"
                    style={{ maxWidth: "400px" }}
                    alt="No data"
                  />
                  <p>No data found...</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Row>

      {tableData?.length > 0 && !showSpinner && (
        <Row>
          <Col className="d-flex justify-content-start disabled">
            Displaying {tableData.length} of {paginationData.total_records} Records
          </Col>
          <Col className="d-flex justify-content-end">
            <Paginations
              current_page={paginationData.current_page}
              page_size={paginationData.page_size}
              total_page={paginationData.total_page}
              total_records={paginationData.total_records}
              clicked_page={handlePageChange}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ContentDataTable;
